import { useEffect, useRef } from 'react';

import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { useQuery } from '@tanstack/react-query';
import { getTranslate } from 'react-localize-redux';
import { useHistory } from 'react-router-dom';

import { appsActions } from '../actions/apps.actions';
import {
  getOnboardingV3Status,
  getOnboardingV3StatusKey,
} from '../components/OnboardingV3/onboardingV3.service';
import { brandService } from '../components/Settings/Brands/brands.services';
import { notifyError } from '../layouts/Notify/actions';
import { flagService } from '../services';
import { isOrgInPathName } from './utils';

const canShowSetupFromPermissions = (permissions: AppState['permissions']) =>
  permissions.includes('FlipdishStaff') || permissions.includes('ViewOnboarding');

const RedirectOrgToBrand = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isFlagOn = useAppSelector((state) => flagService.isFlagOn(state, 'redirectOrgToBrand'));
  const translate = useAppSelector((state) => getTranslate(state));
  const hasRedirectedRef = useRef(false);
  const isSelectingAppRef = useRef(false);

  const locationUrl = new URL(window.location.href);
  let orgId = '';

  if (isOrgInPathName(locationUrl.pathname)) {
    orgId = locationUrl.pathname.slice(1);
  }

  const {
    data: brandsResponse,
    isFetched,
    isLoading,
    error: brandsForOrgError,
  } = useQuery({
    queryKey: [brandService.getBrandsForOrgKey, orgId],
    queryFn: () => brandService.getBrandsForOrg(orgId),
    enabled: !!orgId && isFlagOn,
  });

  const firstBrandId = brandsResponse?.data?.data?.length
    ? brandsResponse.data.data[0].brandId
    : undefined;

  const { data: onboardingStatusData, isFetched: isOnboardingStatusFetched } = useQuery({
    queryKey: [orgId, getOnboardingV3StatusKey],
    queryFn: () => getOnboardingV3Status(orgId),
    enabled: !!firstBrandId && isFetched && !isLoading,
  });

  useEffect(() => {
    if (
      !orgId ||
      !isFetched ||
      isLoading ||
      hasRedirectedRef.current ||
      isSelectingAppRef.current
    ) {
      return;
    }

    if (!firstBrandId) {
      if (!brandsForOrgError) {
        dispatch(
          notifyError({
            message: translate('No_brands_found', { orgId }) as string,
            translate: false,
          })
        );
      }
      return;
    }

    // Do not wait for Redux permissions here: on a cold /orgId visit AuthRouter skips
    // setCurrentApp, so permissions stay empty until we call setCurrentApp below.
    if (!isOnboardingStatusFetched) return;

    isSelectingAppRef.current = true;
    const isInProgress = onboardingStatusData?.data?.status === 'in_progress';
    (async () => {
      try {
        const selectedApp = await dispatch(appsActions.setCurrentApp(firstBrandId));
        if (!selectedApp?.AppId) {
          // setCurrentApp already notifies / may fall back to '/'; do not invent a brand route.
          return;
        }

        const permissions = await dispatch(
          (_: unknown, getState: () => AppState) => getState().permissions
        );
        hasRedirectedRef.current = true;
        if (isInProgress && canShowSetupFromPermissions(permissions)) {
          history.replace(`/${selectedApp.AppId}/setup`);
        } else {
          history.replace(`/${selectedApp.AppId}/home`);
        }
      } finally {
        isSelectingAppRef.current = false;
      }
    })();
  }, [
    orgId,
    firstBrandId,
    isFetched,
    isLoading,
    brandsForOrgError,
    isOnboardingStatusFetched,
    onboardingStatusData,
    dispatch,
    history,
    translate,
  ]);

  useEffect(() => {
    const brandsForOrgErrorMessage = brandsForOrgError?.message;
    if (brandsForOrgErrorMessage) {
      dispatch(notifyError({ message: brandsForOrgErrorMessage, translate: false }));
    }
  }, [brandsForOrgError, dispatch]);

  return null;
};

export default RedirectOrgToBrand;
